import React, { useEffect, useState } from 'react';
import { ReactComponent as MenuIcon } from 'assets/icons/menu.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import { ReactComponent as BlacklistRiderIcon } from 'assets/icons/blacklist-rider.svg';
import { ReactComponent as NoteIcon } from 'assets/icons/note.svg';
import { ReactComponent as EmptyIcon } from 'assets/icons/empty-search-result.svg';
import RiderDetailsSideModal from '../logistics/partners/partner-modals/rider-details-modal';
import BlacklistRiderModal from 'components/blacklist-rider-modal';
import DeleteRiderModal from '../logistics/partners/partner-modals/delete-rider-modal';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from 'utils/api';
import OnlineStatus from 'components/base/online-status';
import { Dropdown, Table, TableData, TableRow } from '@cokitchen/cokitchen-components';
import RiderResultMobile from './rider-result-mobile';
import { RiderType } from 'models/rider.model';
import { BaseDropdownListType, withDropdownList } from 'components/base/dropdown-list';
import { getUserName } from 'utils/functions';

export type DropdownListType = BaseDropdownListType & {
  key: string;
};

const DropdownList = withDropdownList<DropdownListType>();

const TABLE_HEADERS = ['No', 'Rider name', 'Phone number', 'Email', 'Status', ''];
const RiderResultTable = () => {
  const [selectedRider, setSelectedRider] = useState({} as RiderType);
  const [showDeleteRiderModal, setShowDeleteRiderModal] = useState(false);
  const [showBlacklistRiderModal, setShowBlacklistRiderModal] = useState(false);
  const [showRiderDetails, setShowRiderDetails] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [localData, setLocalData] = useState<RiderType[]>([]);
  const [pageNumber, setPageNumber] = useState(1);

  const { filterType: filter_type, filterValue: filter_value } = useParams();
  const navigate = useNavigate();
  if (!filter_value || !filter_type) navigate(-1);

  const fetchListItems = async (refetch?: boolean) => {
    if (!refetch) setLoading(true);
    const filter = { filter_type, filter_value, page: pageNumber };
    const res = await apiService.get(`cs/search/keyword`, {
      params: filter,
      baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
    });
    const { data, total } = res.data;
    setTotal(total);
    setLocalData(data);
    setLoading(false);
  };

  useEffect(() => {
    updatePageNumber({ selected: 0 });
  }, []);

  useEffect(() => {
    if (!showRiderDetails) fetchListItems();
  }, [pageNumber, showRiderDetails]);

  const getListOptions = (rider?: RiderType) => {
    return [
      {
        name: 'View Profile',
        icon: <NoteIcon />,
        key: 'view-profile'
      },
      rider?.rider_blacklist
        ? {
            name: 'Unblacklist rider',
            icon: <BlacklistRiderIcon />,
            key: 'blacklist'
          }
        : {
            name: 'Blacklist rider',
            icon: <BlacklistRiderIcon />,
            key: 'blacklist'
          },
      {
        name: 'Delete Rider',
        icon: <TrashIcon />,
        key: 'delete',
        className: 'text-[#E60A2B]'
      }
    ];
  };

  const viewProfile = () => {
    setShowRiderDetails(!showRiderDetails);
  };
  const blacklistRider = () => {
    setShowBlacklistRiderModal(!showBlacklistRiderModal);
  };
  const deleteRiders = () => {
    setShowDeleteRiderModal(!showDeleteRiderModal);
  };
  const fnMapping: Record<string, () => void> = {
    'view-profile': viewProfile,
    blacklist: blacklistRider,
    delete: deleteRiders
  };

  const handleSelectOption = (option: DropdownListType, rider: RiderType) => {
    setSelectedRider(rider);
    const key = option.key ?? 'view-details';
    fnMapping[key]();
  };

  const updatePageNumber = async (val: { selected: number }) => {
    const pageNo = val?.selected + 1;
    setPageNumber(pageNo);
  };

  const closeRiderBlacklistModal = (refetch?: boolean) => {
    if (refetch) fetchListItems(refetch);
    setShowBlacklistRiderModal(false);
  };

  const closeDeleteRiderModal = (isSuccess?: boolean) => {
    setShowDeleteRiderModal(false);
    if (isSuccess) fetchListItems();
  };

  return (
    <div>
      {localData.length === 0 && !loading ? (
        <div className='flex justify-center items-center'>
          <div>
            <EmptyIcon />
            <p className='py-3'>No search result found</p>
          </div>
        </div>
      ) : (
        <Table
          headers={TABLE_HEADERS}
          pageCount={total / 10}
          loading={loading}
          onPageChange={updatePageNumber}
          hideOnMobile
          mobileContent={<RiderResultMobile riders={localData} onSelect={handleSelectOption} />}>
          {localData?.map((rider, index) => {
            return (
              <TableRow key={index}>
                <TableData className='text-left py-6'>{index + 1}</TableData>
                <TableData>{getUserName(rider)}</TableData>
                <TableData>{rider.phone_number}</TableData>
                <TableData>{rider.email}</TableData>
                <TableData>
                  <OnlineStatus online={rider.online} />
                </TableData>
                <TableData>
                  <Dropdown
                    content={
                      <div className='flex justify-center items-center w-6 rounded-full py-[10px] hover:bg-[#FEF0F2]'>
                        <div className='rotate-90'>
                          <MenuIcon />
                        </div>
                      </div>
                    }>
                    <DropdownList
                      list={getListOptions(rider)}
                      onSelect={(option) => handleSelectOption(option, rider)}
                      className='min-w-[200px]'
                    />
                  </Dropdown>
                </TableData>
              </TableRow>
            );
          })}
        </Table>
      )}
      <DeleteRiderModal
        show={showDeleteRiderModal}
        onClose={closeDeleteRiderModal}
        riders={[selectedRider]}
      />

      <BlacklistRiderModal
        show={showBlacklistRiderModal}
        onClose={closeRiderBlacklistModal}
        rider={selectedRider}
      />
      <RiderDetailsSideModal
        rider={selectedRider}
        show={showRiderDetails}
        onClose={() => setShowRiderDetails(false)}
      />
    </div>
  );
};

export default RiderResultTable;
